import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Cloud,
  CloudDrizzle,
  CloudRain,
  Eye,
  Moon,
  PanelRightClose,
  PanelRightOpen,
  Settings,
  Snowflake,
  Sun,
  Zap,
} from 'lucide-react'
import React, { useEffect, useMemo, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { useNavigate, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import { useAuth } from '../../contexts/AuthContext'
import { useCalendarEntries } from '../../hooks/useCalendarEntries'
import { useCalendarUrl } from '../../hooks/useCalendarUrl'
import { FeatureFlagKey, useFeatureFlags } from '../../hooks/useFeatureFlags'
import { useTrainingWeeks } from '../../hooks/useTrainingWeeks'
import { useUserSettings } from '../../hooks/useUserSettings'
import { useWeather } from '../../hooks/useWeather'
import { useWorkoutGeneration } from '../../hooks/useWorkoutGeneration'
import { useWorkoutRecovery } from '../../hooks/useWorkoutRecovery'
import { useWorkouts, type WorkoutCreate, type WorkoutUpdate } from '../../hooks/useWorkouts'
import { supabase } from '../../lib/supabase'
import { theme } from '../../theme/theme'
import type { DailySleepData } from '../../types/athlete'
import type { CalendarEntryType } from '../../types/calendarEntry'
import type { CalendarEntry, Workout } from '../../types/database.tables'
import type { WorkoutWithData } from '../../types/workout'
import { formatDateForComparison, formatDateToISO, formatDurationMin } from '../../utils/dateUtils'
import { getEntryIcon } from '../../utils/disciplineIcons'
import { Card } from '../common/Card'
import { NavButton } from '../common/NavButton'
import { WorkoutAnalysisView } from '../views/WorkoutAnalysisView'
import { CalendarShareButton } from './CalendarShareButton'
import { type GenerateWorkoutConstraints, GenerateWorkoutConstraintsModal } from './GenerateWorkoutConstraintsModal'
import { WeekSummaryPanel } from './WeekSummaryPanel'
import { WorkoutDetailModal } from './WorkoutDetailModal'
import { WorkoutFormModal } from './WorkoutFormModal'
import { WorkoutFullSizeForm } from './WorkoutFullSizeForm'
import { WorkoutItem } from './WorkoutItem'

const CalendarContainer = styled.div`
  width: 100%;
`

const DesktopCard = styled(Card)`
  @media (max-width: 768px) {
    background: none;
    border: none;
    padding: 0;
    box-shadow: none;
  }
`

const SkeletonWrapper = styled.div`
  .react-loading-skeleton {
    --base-color: ${({ theme }) => theme.colors.surface};
    --highlight-color: ${({ theme }) => theme.colors.border};
    border-radius: ${theme.borderRadius.md};
  }
`

const CalendarWithSummary = styled.div`
  display: flex;
  gap: ${theme.spacing.lg};
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: ${theme.spacing.md};
  }
`

const CalendarSection = styled.div`
  flex: 1;
  min-width: 0;
  width: 100%;
`

const CalendarHeader = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.md};
  padding-bottom: ${theme.spacing.sm};

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: ${theme.spacing.xs};
    margin-bottom: ${theme.spacing.sm};
  }
`

const CalendarNavigation = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: ${theme.spacing.xs};
    width: 100%;
  }
`

const TrainingWeekTitle = styled.div`
  border-radius: ${theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.surfaceHover};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  font-size: ${theme.typography.xs};

  @media (max-width: 768px) {
    font-size: 10px;
    padding: 4px ${theme.spacing.xs};
    white-space: nowrap;
  }
`
const Spacer = styled.div`
  flex-grow: 1;

  @media (max-width: 768px) {
    display: none;
  }
`

const CalendarTitle = styled.h3`
  font-size: ${theme.typography.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
  min-width: 150px;
  text-align: center;

  @media (max-width: 768px) {
    font-size: ${theme.typography.md};
    min-width: 120px;
    flex-grow: 1;
  }
`

const CalendarControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: ${theme.spacing.xs};
    display: none;
  }
`

const ViewModeDropdown = styled.div<{ $disabled: boolean }>`
  position: relative;

  /* prevent cursor events when dropdown is disabled */
  ${(props) =>
    props.$disabled &&
    `
  pointer-events: none;
  opacity: 0.5;
  `}
`

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${theme.typography.sm};
  cursor: pointer;
  transition: ${theme.transitions.default};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: ${theme.spacing.xs};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  box-shadow: ${({ theme }) => theme.shadows.md};
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
  transform: ${(props) => (props.$isOpen ? 'scale(1)' : 'scale(0.95)')};
  transition: ${theme.transitions.default};
  z-index: 1000;
  min-width: 120px;
`

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text};
  font-size: ${theme.typography.sm};
  text-align: left;
  cursor: pointer;
  transition: ${theme.transitions.default};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }

  &:first-child {
    border-radius: ${theme.borderRadius.sm} ${theme.borderRadius.sm} 0 0;
  }

  &:last-child {
    border-radius: 0 0 ${theme.borderRadius.sm} ${theme.borderRadius.sm};
  }
`

const SettingsButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  transition: ${theme.transitions.default};
  position: relative;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

const SettingsDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: ${theme.spacing.xs};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  box-shadow: ${({ theme }) => theme.shadows.md};
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
  transform: ${(props) => (props.$isOpen ? 'scale(1)' : 'scale(0.95)')};
  transition: ${theme.transitions.default};
  z-index: 1000;
  min-width: 180px;
  padding: ${theme.spacing.sm};
`

const SettingsItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing.sm};
  font-size: ${theme.typography.sm};
  color: ${({ theme }) => theme.colors.text};

  .setting-label {
    color: ${({ theme }) => theme.colors.text};
  }

  .setting-control {
    display: flex;
    align-items: center;
    gap: ${theme.spacing.xs};
  }
`

const ToggleSwitch = styled.button<{ $isOn: boolean }>`
  width: 40px;
  height: 20px;
  background: ${(props) => (props.$isOn ? theme.colors.primary : theme.colors.border)};
  border: none;
  border-radius: 10px;
  position: relative;
  cursor: pointer;
  transition: ${theme.transitions.default};

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${(props) => (props.$isOn ? '22px' : '2px')};
    width: 16px;
    height: 16px;
    background: white;
    border-radius: 50%;
    transition: ${theme.transitions.default};
  }
`

const ReplanButton = styled.button<{ $loading?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  font-size: ${theme.typography.sm};
  cursor: pointer;
  transition: ${theme.transitions.default};
  position: relative;
  overflow: hidden;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.surfaceHover};
    border: 1px solid ${({ theme }) => theme.colors.primary};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }

  ${(props) =>
    props.$loading
      ? `
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 20px;
      height: 20px;
      margin: -10px 0 0 -10px;
      border: 2px solid transparent;
      border-top: 2px solid #000000;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    span {
      opacity: 0;
    }
  `
      : ''}

  @media (max-width: 768px) {
    font-size: ${theme.typography.xs};
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
  }
`

const SummaryToggleButton = styled.button<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: ${({ theme, $isActive }) => ($isActive ? theme.colors.surface : theme.colors.surfaceHover)};
  border: 1px solid ${({ theme, $isActive }) => ($isActive ? theme.colors.primary : theme.colors.border)};
  border-radius: ${theme.borderRadius.md};
  color: ${({ theme, $isActive }) => ($isActive ? theme.colors.text : theme.colors.text)};
  cursor: pointer;
  transition: ${theme.transitions.default};

  &:hover {
    background: ${({ theme, $isActive }) => ($isActive ? theme.colors.surface : theme.colors.surfaceHover)};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 1px;
  background-color: ${({ theme }) => theme.colors.border};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  overflow: hidden;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.sm};
    background-color: transparent;
    border: none;
  }
`

const DayColumn = styled.div<{
  $isDragOver?: boolean
}>`
  background-color: ${({ theme }) => theme.colors.cardBackground};
  min-height: 200px;
  display: flex;
  flex-direction: column;
  position: relative;
  min-width: 0; /* Allow shrinking below content width */
  overflow: hidden; /* Prevent content overflow */
  border: 2px solid transparent;

  ${({ theme, $isDragOver }) =>
    $isDragOver &&
    `
    background-color: ${theme.colors.surface};
    border: 2px solid ${theme.colors.primaryLight};
  `}

  @media (max-width: 768px) {
    border: 0;
    background-color: transparent;
    border-radius: ${theme.borderRadius.md};
    min-height: auto;
  }
`

const DayHeader = styled.div<{ $isToday?: boolean }>`
  padding: ${theme.spacing.sm};
  background-color: ${({ theme, $isToday }) => ($isToday ? theme.colors.secondaryLight : theme.colors.surface)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  text-align: center;
  position: relative;
  transition: ${theme.transitions.default};
  z-index: 2;
  min-height: 103px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: ${theme.spacing.xs};
  overflow: visible;

  ${(props) =>
    props.$isToday &&
    `
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 2px;
      background-color: ${theme.colors.secondaryDark};
    }
  `}

  @media (max-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    min-height: auto;
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
    text-align: left;
    margin-bottom: ${theme.spacing.xs};
    border-radius: ${theme.borderRadius.sm} ${theme.borderRadius.sm} ${theme.borderRadius.sm} ${theme.borderRadius.sm};
    border-bottom: none;
    background-color: ${({ theme, $isToday }) => ($isToday ? theme.colors.primary : theme.colors.cardBackground)};

    ${(props) =>
      props.$isToday &&
      `
        &::after {
          content: none;
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
        }
      `}
}
`

const DayHeaderTop = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: row;
    gap: ${theme.spacing.sm};
    align-items: baseline;
  }
`

const DayHeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
  flex: 1;
  overflow: visible;

  @media (max-width: 768px) {
    flex-direction: row;
    flex-wrap: wrap;
    min-height: auto;
    align-items: center;
    margin-left: 40%;
  }
`

const AllDayEvent = styled.div<{ $isToday?: boolean }>`
  background-color: ${({ theme }) => theme.colors.surfaceHover};
  border-radius: ${theme.borderRadius.sm};
  padding: 2px ${theme.spacing.xs};
  font-size: ${theme.typography.xs};
  color: ${({ theme, $isToday }) => ($isToday ? theme.colors.text : theme.colors.text)};
  cursor: pointer;
  display: flex;
  transition: ${theme.transitions.default};
  text-align: left;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0; /* Allow shrinking below content width */

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
    opacity: 0.8;
  }
`

const HeaderActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 2px;
  opacity: 0;
  transition: ${theme.transitions.default};

  ${DayColumn}:hover & {
    opacity: 1;
  }
`

const DayNumber = styled.div<{ $isToday?: boolean }>`
  color: ${({ theme, $isToday }) => ($isToday ? theme.colors.textInverted : theme.colors.text)};
  font-weight: 600;
  font-size: ${theme.typography.lg};
  margin-bottom: ${theme.spacing.xs};
`

const DayName = styled.div<{ $isToday?: boolean }>`
  color: ${({ theme, $isToday }) => ($isToday ? theme.colors.textInverted : theme.colors.text)};
  font-size: ${theme.typography.xs};
  margin-bottom: ${theme.spacing.xs};
`

const SleepIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  width: 100%;
  min-height: 26px;
  position: relative;
  cursor: help;
  z-index: 10;

  @media (max-width: 768px) {
    max-width: 100px;
  }
`

const SleepIcon = styled.div<{ $isToday?: boolean }>`
  flex-shrink: 0;
  color: ${({ theme, $isToday }) => ($isToday ? theme.colors.textInverted : theme.colors.textSecondary)};
  opacity: 0.7;
`

const SleepBar = styled.div<{ $duration: number; $isToday?: boolean }>`
  flex: 1;
  height: 8px;
  background: ${({ theme }) => theme.colors.cardBackground};
  border-radius: 4px;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: ${(props) => Math.min((props.$duration / 10) * 100, 100)}%;
    background: ${({ theme, $duration }) => {
      if ($duration >= 7) {
        return theme.colors.success
      } else if ($duration >= 6) {
        return theme.colors.warning
      } else {
        return theme.colors.error
      }
    }};
    border-radius: 4px;
    transition: width 0.3s ease;
  }
`

const SleepDuration = styled.span<{ $isToday?: boolean }>`
  font-size: ${theme.typography.xs};
  color: ${({ theme, $isToday }) => ($isToday ? theme.colors.textInverted : theme.colors.textSecondary)};
  font-weight: 500;
  white-space: nowrap;
  margin-left: 4px;

  @media (max-width: 768px) {
    font-size: 10px;
  }
`

const SleepTooltip = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: ${({ theme }) => theme.colors.text};
  color: ${({ theme }) => theme.colors.background};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.xs};
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
  z-index: 10001;
  margin-top: 4px;
  text-align: left;

  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-bottom-color: ${({ theme }) => theme.colors.text};
  }

  ${SleepIndicator}:hover & {
    opacity: 1;
  }
`

const RestDay = styled.div<{ $isToday?: boolean }>`
  display: flex;
  gap: ${theme.spacing.xs};
  width: 100%;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme, $isToday }) => ($isToday ? 'rgba(255, 255, 255, 0.2)' : theme.colors.surfaceHover)};
    opacity: 1;
  }
`

const AllDayIcon = styled.div<{ $isToday?: boolean }>`
  flex-shrink: 0;
  color: ${({ theme, $isToday }) => ($isToday ? theme.colors.textSecondary : theme.colors.textSecondary)};
  margin-top: 2px;
`

const AllDayText = styled.div<{ $isToday?: boolean }>`
  color: ${({ theme, $isToday }) => ($isToday ? theme.colors.textInverted : theme.colors.text)};
  font-size: ${theme.typography.xs};
  margin-left: ${theme.spacing.sm};
  flex-grow: 1;
  text-overflow: ellipsis;
  overflow-x: hidden;
`

const WeatherContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  font-size: ${theme.typography.xs};
  min-height: 26px;

  @media (max-width: 768px) {
    min-width: 50px;
  }
`

const WeatherIcon = styled.div<{ $isToday?: boolean }>`
  color: ${({ theme, $isToday }) => ($isToday ? theme.colors.textInverted : theme.colors.textSecondary)};
  opacity: 0.8;
  display: flex;
  align-items: center;
`

const WeatherTemp = styled.div<{ $isToday?: boolean }>`
  color: ${({ theme, $isToday }) => ($isToday ? theme.colors.textInverted : theme.colors.text)};
  font-weight: 500;
`

const DayContent = styled.div`
  flex: 1;
  padding: ${theme.spacing.xs};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
  position: relative;
  z-index: 1;
  min-width: 0; /* Allow shrinking below content width */
  overflow: hidden; /* Prevent content overflow */

  @media (max-width: 768px) {
    padding: 0;
    min-height: 60px;
  }
`

const AddEntryButton = styled.button`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.textMuted};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  cursor: pointer;
  font-size: ${theme.typography.xs};
  font-weight: 500;
  opacity: 0;
  transition: ${theme.transitions.slow};
  z-index: 10;

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderHover};
    color: ${({ theme }) => theme.colors.textSecondary};
    background-color: ${({ theme }) => theme.colors.surfaceHover};
  }

  ${DayColumn}:hover & {
    opacity: 1;
  }
`

const RecoveryIndicator = styled.div<{ $recoveryLevel: number }>`
  height: 4px;
  width: ${(props) => Math.max(0, props.$recoveryLevel * 100)}%;
  background: linear-gradient(
    90deg,
    rgba(255, 107, 107, 0.8) 0%,
    rgba(255, 107, 107, 0.4) 70%,
    rgba(255, 107, 107, 0.1) 100%
  );
  border-radius: 2px;
  margin-bottom: ${theme.spacing.xs};
  transition: width 0.3s ease;
  display: ${(props) => (props.$recoveryLevel > 0.05 ? 'block' : 'none')};
  opacity: ${(props) => (props.$recoveryLevel > 0.05 ? 1 : 0)};
  position: relative;
  cursor: help;
`

const Tooltip = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: ${({ theme }) => theme.colors.text};
  color: ${({ theme }) => theme.colors.background};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.xs};
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
  z-index: 1000;
  margin-bottom: 4px;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: ${({ theme }) => theme.colors.text};
  }

  ${RecoveryIndicator}:hover & {
    opacity: 1;
  }
`

const EmptyDay = styled.div<{ $isDragOver?: boolean }>`
  text-align: center;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${theme.typography.xs};
  padding: ${theme.spacing.lg} ${theme.spacing.sm};
  border: 2px dashed transparent;
  border-radius: ${theme.borderRadius.sm};
  transition: ${theme.transitions.default};
  position: absolute;
  width: 100%;

  ${(props) =>
    props.$isDragOver &&
    `
    border-color: ${theme.colors.textSecondary};
    background-color: ${theme.colors.surfaceHover};
    color: ${theme.colors.text};
  `}
`

const DropIndicator = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${theme.typography.xs};
  font-weight: 500;
  margin-top: ${theme.spacing.sm};
  opacity: 0.8;
`

// Helper function to get the Monday of the current week
const getCurrentWeekStartDate = (): Date => {
  const today = new Date()
  const currentDay = today.getDay()
  const startOfWeek = new Date(today)
  startOfWeek.setDate(today.getDate() - (currentDay === 0 ? 6 : currentDay - 1))
  startOfWeek.setHours(0, 0, 0, 0) // Reset time to start of day
  return startOfWeek
}

// Helper function to get all dates for a week starting from a given Monday
const getWeekDatesFromStart = (startDate: Date): Date[] => {
  const weekDates = []
  for (let i = 0; i < 7; i++) {
    const date = new Date(startDate)
    date.setDate(startDate.getDate() + i)
    weekDates.push(date)
  }
  return weekDates
}

const formatDayName = (date: Date): string => {
  return date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()
}

const formatDateNumber = (date: Date): string => {
  return date.getDate().toString()
}

const getWeatherIcon = (weatherMain: string, weatherIcon: string): React.ComponentType<{ size?: number }> => {
  // Map weather conditions to Lucide icons
  const iconMap: Record<string, React.ComponentType<{ size?: number }>> = {
    Clear: Sun,
    Clouds: Cloud,
    Rain: CloudRain,
    Drizzle: CloudDrizzle,
    Thunderstorm: Zap,
    Snow: Snowflake,
    Mist: Eye,
    Fog: Eye,
    Haze: Eye,
  }

  return iconMap[weatherMain] || Cloud
}

type WeeklyCalendarCardProps = {
  athleteId?: string
}

export const WeeklyCalendarCard: React.FC<WeeklyCalendarCardProps> = ({ athleteId }) => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // Initialize week from URL parameter or default to current week
  const getInitialWeekStartDate = (): Date => {
    const weekParam = searchParams.get('week')
    if (weekParam) {
      const parsedDate = new Date(weekParam)
      // Validate that the date is valid
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate
      }
    }
    return getCurrentWeekStartDate()
  }

  const [currentWeekStartDate, setCurrentWeekStartDate] = useState(getInitialWeekStartDate())
  const [draggedWorkout, setDraggedWorkout] = useState<string | null>(null)
  const [dragOverDay, setDragOverDay] = useState<string | null>(null)
  const [mouseDownTime, setMouseDownTime] = useState<number | null>(null)
  const [viewModeDropdownOpen, setViewModeDropdownOpen] = useState(false)
  const [settingsDropdownOpen, setSettingsDropdownOpen] = useState(false)
  const [calendarShareDropdownOpen, setCalendarShareDropdownOpen] = useState(false)
  const [viewMode, setViewMode] = useState<'Week' | 'Month'>('Week')
  const [sleepData, setSleepData] = useState<DailySleepData[]>([])
  const [athleteProfile, setAthleteProfile] = useState<{
    first_name: string | null
    last_name: string | null
    email: string
  } | null>(null)
  const [isSyncing, setIsSyncing] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)

  const { settings, updateSetting } = useUserSettings()
  const { isFeatureEnabled } = useFeatureFlags()
  const [modalState, setModalState] = useState<{
    isOpen: boolean
    mode: 'create' | 'edit'
    workout?: Workout
    calendarEntry?: CalendarEntry
    defaultDate?: string
    entryType?: CalendarEntryType
  }>({
    isOpen: false,
    mode: 'create',
  })

  const [fullSizeState, setFullSizeState] = useState<{
    isOpen: boolean
    mode: 'create' | 'edit'
    workout?: Workout
    formData?: any
    defaultDate?: string
  }>({
    isOpen: false,
    mode: 'create',
  })

  const [detailModalState, setDetailModalState] = useState<{
    isOpen: boolean
    workout?: WorkoutWithData
    entry?: CalendarEntry
  }>({
    isOpen: false,
  })

  const [analysisViewState, setAnalysisViewState] = useState<{
    isOpen: boolean
    workout?: WorkoutWithData
  }>({
    isOpen: false,
  })

  const [constraintsModalState, setConstraintsModalState] = useState<{
    isOpen: boolean
    constraints?: GenerateWorkoutConstraints
  }>({ isOpen: false })

  const [entryModalState, setEntryModalState] = useState<{
    isOpen: boolean
    selectedDate?: Date
    editEntry?: CalendarEntry
  }>({ isOpen: false })

  const [entryType, setEntryType] = useState<'workout' | 'rest_day' | 'event' | 'note'>('workout')
  const [showSummaryPanel, setShowSummaryPanel] = useState(settings.showCalendarSummary)
  const [showWeatherIndicator, setShowWeatherIndicator] = useState(settings.showWeatherIndicator)
  const [isNavigating, setIsNavigating] = useState(false)
  const { user } = useAuth()
  const {
    workouts,
    isLoading: isLoadingWorkouts,
    error: workoutsError,
    loadWorkouts,
    createWorkout,
    deleteWorkout,
    updateWorkout,
  } = useWorkouts(athleteId)
  const { generateWorkouts, isGenerating } = useWorkoutGeneration()
  const { calculateWorkoutRecoveryData, formatRecoveryHours } = useWorkoutRecovery()
  const { trainingWeek, loadTrainingWeek } = useTrainingWeeks()
  const { calendarUrl, isLoading: isLoadingCalendarUrl, generateCalendarUrl, copyToClipboard } = useCalendarUrl()
  const { getWeatherForDate } = useWeather({
    locationLatitude: 47.2261376,
    locationLongitude: 8.9482314,
  })
  const { loadEntries, deleteEntry, getEntriesForDate } = useCalendarEntries()

  const weekDates = useMemo(() => {
    return getWeekDatesFromStart(currentWeekStartDate)
  }, [currentWeekStartDate])

  useEffect(() => {
    setShowSummaryPanel(settings.showCalendarSummary)
  }, [settings])

  // Handle window resize for mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Fetch athlete profile if viewing another athlete's calendar
  useEffect(() => {
    const fetchAthleteProfile = async () => {
      if (athleteId && user && athleteId !== user.id) {
        try {
          const { data, error } = await supabase
            .from('profiles')
            .select('first_name, last_name, email')
            .eq('id', athleteId)
            .single()

          if (error) {
            console.error('Error fetching athlete profile:', error)
            return
          }

          setAthleteProfile(data)
        } catch (err) {
          console.error('Exception fetching athlete profile:', err)
        }
      } else {
        setAthleteProfile(null)
      }
    }

    fetchAthleteProfile()
  }, [athleteId, user])

  // Load workouts when component mounts or user changes (but not when week changes)
  useEffect(() => {
    if (user) {
      const startDate = weekDates[0]
      const endDate = weekDates[6]
      loadWorkouts(startDate, endDate)

      // Also load training week data
      loadTrainingWeek(startDate)

      // Load calendar entries for the week
      loadEntries(startDate, endDate)

      // Load sleep summaries for the week
      loadSleepData(startDate, endDate)
    }
  }, [user, loadWorkouts, loadTrainingWeek, loadEntries])

  // Function to load sleep data for the week
  const loadSleepData = async (startDate: Date, endDate: Date) => {
    try {
      const userId = athleteId || user?.id
      if (!userId) return

      const { data, error } = await supabase
        .from('sleep_summaries')
        .select('calendar_date, duration_seconds, start_time_seconds')
        .eq('user_id', userId)
        .gte('calendar_date', formatDateForComparison(startDate))
        .lte('calendar_date', formatDateForComparison(endDate))

      if (error) {
        console.error('Error loading sleep data:', error)
        return
      }

      // Transform to DailySleepData format
      const sleepDataArray: DailySleepData[] = (data || []).map((item) => {
        // Convert Unix timestamp to Date and format as HH:MM
        let bedtime: string | undefined
        if (item.start_time_seconds) {
          const bedtimeDate = new Date(item.start_time_seconds * 1000)
          bedtime = bedtimeDate.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          })
        }

        return {
          date: item.calendar_date,
          duration: item.duration_seconds / 3600, // Convert seconds to hours
          quality: 0.8, // Default quality, can be enhanced later
          sleepScore: 75, // Default score, can be enhanced later
          bedtime,
        }
      })

      setSleepData(sleepDataArray)
    } catch (err) {
      console.error('Exception loading sleep data:', err)
    }
  }

  // Helper function to get sleep data for a specific date
  const getSleepDataForDate = (date: Date): DailySleepData | undefined => {
    const dateString = formatDateForComparison(date)
    return sleepData.find((sleep) => sleep.date === dateString)
  }

  // Listen for workout upload events and reload calendar data
  useEffect(() => {
    const handleWorkoutUploaded = () => {
      if (user) {
        const startDate = weekDates[0]
        const endDate = weekDates[6]
        console.log('Workout uploaded event received, reloading calendar data')
        loadWorkouts(startDate, endDate)
        loadEntries(startDate, endDate)
      }
    }

    window.addEventListener('workout-uploaded', handleWorkoutUploaded)
    return () => {
      window.removeEventListener('workout-uploaded', handleWorkoutUploaded)
    }
  }, [user, weekDates, loadWorkouts, loadEntries])

  const handleDragStart = (e: React.DragEvent, workoutId: string) => {
    console.log('handleDragStart', workoutId)
    e.dataTransfer.setData('text/plain', workoutId)
    setDraggedWorkout(workoutId)
    // Small delay to distinguish from click
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragEnd = () => {
    setDraggedWorkout(null)
    setDragOverDay(null)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const handleDragEnter = (e: React.DragEvent, date: string) => {
    e.preventDefault()
    setDragOverDay(date)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear drag over if we're leaving the drop zone completely
    if (!(e.currentTarget as Element).contains(e.relatedTarget as Node)) {
      setDragOverDay(null)
    }
  }

  const handleDrop = async (e: React.DragEvent, targetDate: string) => {
    e.preventDefault()
    const workoutId = e.dataTransfer.getData('text/plain')
    console.log('handleDrop', workoutId, targetDate)

    if (workoutId) {
      const workoutData = workouts.find((w) => w.scheduled_date === workoutId)
      // TODO set targetDate
      console.log('handleDrop/updateWorkout', workoutData)
      if (workoutData) {
        await updateWorkout(workoutId, workoutData)
      }
    }

    setDraggedWorkout(null)
    setDragOverDay(null)
  }

  const handleAddWorkout = (date: string) => {
    setModalState({
      isOpen: true,
      mode: 'create',
      entryType: 'workout',
      defaultDate: date,
    })
  }

  const handleEditWorkout = (workout: Workout) => {
    setModalState({
      isOpen: true,
      mode: 'edit',
      entryType: 'workout',
      workout,
    })
  }

  const handleDeleteWorkout = async (workoutId: string) => {
    await deleteWorkout(workoutId)
  }

  const handleEditEntry = (entry: CalendarEntry) => {
    setEntryModalState({
      isOpen: true,
      editEntry: entry,
    })
  }

  const handleDeleteEntry = async (entryId: string) => {
    await deleteEntry(entryId)
  }

  const handleWorkoutMouseDown = (e: React.MouseEvent, workout: WorkoutWithData) => {
    setMouseDownTime(Date.now())
  }

  const handleWorkoutClick = (e: React.MouseEvent, workout: WorkoutWithData) => {
    // Don't trigger click if user is trying to drag or if dragging occurred
    if (draggedWorkout === workout.id) return

    // If mouse was held down for more than 200ms, likely a drag attempt
    const holdTime = mouseDownTime ? Date.now() - mouseDownTime : 0
    if (holdTime > 200) return

    e.preventDefault()
    e.stopPropagation()

    // Open analysis view for completed workouts, otherwise detail modal
    if (workout.status === 'completed') {
      // If viewing another athlete's calendar, navigate to their workout URL
      if (athleteId) {
        const weekParam = formatDateToISO(currentWeekStartDate).split('T')[0]
        navigate(`/athletes/${athleteId}/workouts/${workout.id}?week=${weekParam}`)
      } else {
        setAnalysisViewState({
          isOpen: true,
          workout,
        })
      }
    } else {
      setDetailModalState({
        isOpen: true,
        workout,
        entry: undefined,
      })
    }
  }

  const handleFullSize = (data: {
    formData: any
    entryType: string
    initialData?: Workout
    initialEntry?: CalendarEntry
  }) => {
    if (data.entryType === 'workout') {
      setFullSizeState({
        isOpen: true,
        mode: data.initialData ? 'edit' : 'create',
        workout: data.initialData,
        formData: data.formData,
        defaultDate: data.formData.scheduled_date,
      })

      // Close the modal
      setModalState({ isOpen: false, mode: 'create' })
    }
  }

  const handleFullSizeCreate = async (workoutData: WorkoutCreate) => {
    await createWorkout(workoutData)
    setFullSizeState({ isOpen: false, mode: 'create' })
  }

  const handleFullSizeUpdate = async (workoutData: WorkoutUpdate) => {
    if (!fullSizeState.workout) {
      return
    }
    await updateWorkout(fullSizeState.workout.id, workoutData)
    setFullSizeState({ isOpen: false, mode: 'create' })
  }

  const closeFullSize = () => {
    setFullSizeState({ isOpen: false, mode: 'create' })
  }

  const handleEntryClick = (entry: CalendarEntry) => {
    setDetailModalState({
      isOpen: true,
      workout: undefined,
      entry,
    })
  }

  const handleDetailEdit = () => {
    if (detailModalState.workout) {
      // Open full-size form for workout editing
      setFullSizeState({
        isOpen: true,
        mode: 'edit',
        workout: detailModalState.workout,
        formData: detailModalState.workout,
        defaultDate: detailModalState.workout.scheduled_date,
      })
    } else if (detailModalState.entry) {
      // Open modal for entry editing
      setModalState({
        isOpen: true,
        mode: 'edit',
        entryType: detailModalState.entry.entry_type as CalendarEntryType,
        workout: undefined,
        calendarEntry: detailModalState.entry,
        defaultDate: detailModalState.entry.date,
      })
    }

    // Close detail modal
    setDetailModalState({ isOpen: false })
  }

  const handleDetailDelete = async () => {
    console.log('handleDetailDelete')
    if (detailModalState.workout) {
      await deleteWorkout(detailModalState.workout.id)
    } else if (detailModalState.entry) {
      await deleteEntry(detailModalState.entry.id)
    }
    setDetailModalState({ isOpen: false })
  }

  const closeDetailModal = () => {
    setDetailModalState({ isOpen: false })
  }

  const closeAnalysisView = () => {
    setAnalysisViewState({ isOpen: false })
  }

  const handleWorkoutCompletion = async (workoutId: string, completed: boolean) => {
    try {
      // Find the current workout
      const currentWorkout = workouts.find((w) => w.id === workoutId)
      if (!currentWorkout) return

      // Update the workout status
      const updatedWorkout = {
        ...currentWorkout,
        status: completed ? ('completed' as const) : ('scheduled' as const),
      }
      delete updatedWorkout.executedTotalDurationSeconds
      delete updatedWorkout.workout_sessions
      await updateWorkout(workoutId, updatedWorkout as Workout)
    } catch (error) {
      console.error('Failed to update workout completion status:', error)
    }
  }

  const handleSchedule = async (workoutId: string, scheduledTime: string) => {
    try {
      const updatedWorkout = {
        time: scheduledTime,
      } as Partial<Workout>
      await updateWorkout(workoutId, updatedWorkout as Workout)
    } catch (error) {
      console.error('Failed to schedule workout:', error)
    }
  }

  const closeModal = () => {
    setModalState({ isOpen: false, mode: 'create' })
    // Trigger reloading of workouts to ensure UI consistency
    if (user) {
      const startDate = weekDates[0]
      const endDate = weekDates[6]
      loadWorkouts(startDate, endDate)
    }
  }

  const generateWeeklyWorkouts = () => {
    const constraints = getGenerateWorkoutConstraints()
    setConstraintsModalState({
      isOpen: true,
      constraints,
    })
  }

  const handleConstraintsSubmit = async (constraints: GenerateWorkoutConstraints) => {
    setConstraintsModalState({ isOpen: false })
    try {
      await generateWorkouts(constraints)
    } catch (error: unknown) {
      alert('Failed to generate workout plan: ' + (error as any).message)
    }
  }

  const getCurrentWeekString = () => {
    const startDate = weekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    const endDate = weekDates[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    return `${startDate} - ${endDate}`
  }

  // Navigation handlers
  const goToPreviousWeek = () => {
    if (isNavigating) return // Prevent multiple clicks
    if (!user) return

    const newDate = new Date(currentWeekStartDate)
    newDate.setDate(currentWeekStartDate.getDate() - 7)
    changeWeek(newDate)
  }

  const goToNextWeek = () => {
    if (isNavigating) return // Prevent multiple clicks
    if (!user) return

    const newDate = new Date(currentWeekStartDate)
    newDate.setDate(currentWeekStartDate.getDate() + 7)
    changeWeek(newDate)
  }

  const changeWeek = (newDate: Date) => {
    // Pre-load data for the new week before changing the date
    setIsNavigating(true)
    const newWeekDates = getWeekDatesFromStart(newDate)
    const startDate = newWeekDates[0]
    const endDate = newWeekDates[6]

    // Load data in parallel without waiting
    Promise.all([
      loadWorkouts(startDate, endDate),
      loadTrainingWeek(startDate),
      loadEntries(startDate, endDate),
      loadSleepData(startDate, endDate),
    ])
      .then(() => {
        // Only update the date after data is loaded
        setCurrentWeekStartDate(newDate)
        // Update URL with new week parameter (YYYY-MM-DD format)
        const weekParam = formatDateToISO(newDate).split('T')[0]
        setSearchParams({ week: weekParam })
        setIsNavigating(false)
      })
      .catch(() => {
        // Even on error, allow navigation to continue
        setCurrentWeekStartDate(newDate)
        // Update URL with new week parameter (YYYY-MM-DD format)
        const weekParam = formatDateToISO(newDate).split('T')[0]
        setSearchParams({ week: weekParam })
        setIsNavigating(false)
      })
  }

  const goToCurrentWeek = () => {
    changeWeek(getCurrentWeekStartDate())
  }

  // Dropdown handlers
  const toggleViewModeDropdown = (e: React.MouseEvent) => {
    e.stopPropagation()
    setViewModeDropdownOpen(!viewModeDropdownOpen)
    setSettingsDropdownOpen(false)
  }

  const toggleSettingsDropdown = (e: React.MouseEvent) => {
    e.stopPropagation()
    setSettingsDropdownOpen(!settingsDropdownOpen)
    setViewModeDropdownOpen(false)
    setCalendarShareDropdownOpen(false)
  }

  const toggleCalendarShareDropdown = (e: React.MouseEvent) => {
    e.stopPropagation()
    setCalendarShareDropdownOpen(!calendarShareDropdownOpen)
    setViewModeDropdownOpen(false)
    setSettingsDropdownOpen(false)
  }

  const handleViewModeChange = (mode: 'Week' | 'Month') => {
    setViewMode(mode)
    setViewModeDropdownOpen(false)
    // Note: Month view functionality would be implemented here
    console.log(`Switched to ${mode} view`)
  }

  const handleSettingToggle = (setting: keyof typeof settings) => {
    updateSetting(setting, !settings[setting])
  }

  const handleGenerateCalendarUrl = async () => {
    try {
      await generateCalendarUrl()
    } catch (error) {
      console.error('Failed to generate calendar URL:', error)
    }
  }

  const handleCopyCalendarUrl = async () => {
    const success = await copyToClipboard()
    if (success) {
      // Show a temporary success message
      console.log('Calendar URL copied to clipboard!')
    }
  }

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setViewModeDropdownOpen(false)
      setSettingsDropdownOpen(false)
      setCalendarShareDropdownOpen(false)
    }

    if (viewModeDropdownOpen || settingsDropdownOpen || calendarShareDropdownOpen) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [viewModeDropdownOpen, settingsDropdownOpen, calendarShareDropdownOpen])

  const getGenerateWorkoutConstraints = () => {
    // Get the current displayed week dates
    const currentDisplayedWeekDates = getWeekDatesFromStart(currentWeekStartDate)

    // For current week, ensure startDate is no earlier than today
    const today = new Date()
    const currentWeekStart = getCurrentWeekStartDate()
    const isCurrentWeek = formatDateForComparison(currentWeekStartDate) === formatDateForComparison(currentWeekStart)

    let startDate = currentDisplayedWeekDates[0]
    if (isCurrentWeek && formatDateForComparison(startDate) < formatDateForComparison(today)) {
      startDate = today
    }

    const weeksToGoal = 6 // TODO implement
    const maxHardDays = 2 // TODO expose to UI

    // Calculate last week's TSS from workouts
    const previousWeekStartDate = new Date(currentDisplayedWeekDates[0])
    previousWeekStartDate.setDate(previousWeekStartDate.getDate() - 7)
    return {
      startDate: formatDateToISO(startDate),
      endDate: formatDateToISO(currentDisplayedWeekDates[6]),
      trainingPhase: trainingWeek?.training_phase || '',
      focus: 'balanced', // TODO: expose to UI
      bikeSplit: 0.3, // TODO: expose to UI
      swimSplit: 0.3, // TODO: expose to UI
      runSplit: 0.3, // TODO: expose to UI
      includeMobility: true, // TODO: expose to UI
      includeStrength: true, // TODO: expose to UI
      lastWeekTSS: 0,
      maxHardDays,
      plannedWeekHours: trainingWeek?.target_hours,
      // TODO: read from record
      plannedWeekTSS: Math.round((trainingWeek?.target_hours ?? 0) * 125), // Estimate TSS from target hours (125 TSS/hour)
      weekInPlan: trainingWeek?.week_in_plan || 1,
      weeksToGoal,
    } as GenerateWorkoutConstraints
  }

  // Filter and reverse days for mobile view
  const displayDates = useMemo(() => {
    if (!isMobile) return weekDates

    // On mobile: filter out future days and reverse order (most recent first)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return weekDates
      .filter((date) => {
        const compareDate = new Date(date)
        compareDate.setHours(0, 0, 0, 0)
        return compareDate <= today
      })
      .reverse()
  }, [isMobile, weekDates])

  if (fullSizeState.isOpen) {
    return (
      <WorkoutFullSizeForm
        onClose={closeFullSize}
        onCreate={handleFullSizeCreate}
        onUpdate={handleFullSizeUpdate}
        initialData={fullSizeState.workout}
        formData={fullSizeState.formData}
        defaultDate={fullSizeState.defaultDate}
      />
    )
  }
  if (analysisViewState.isOpen && analysisViewState.workout) {
    return <WorkoutAnalysisView workout={analysisViewState.workout} onBack={closeAnalysisView} />
  }
  return (
    <DesktopCard>
      <CalendarHeader>
        <CalendarNavigation>
          <>
            <NavButton onClick={goToPreviousWeek} disabled={isNavigating} title="Previous week">
              <ChevronLeft size={16} />
            </NavButton>
            <CalendarTitle>{getCurrentWeekString()}</CalendarTitle>
            <NavButton onClick={goToNextWeek} disabled={isNavigating} title="Next week">
              <ChevronRight size={16} />
            </NavButton>
          </>
          {
            <NavButton
              onClick={goToCurrentWeek}
              disabled={
                formatDateForComparison(currentWeekStartDate) === formatDateForComparison(getCurrentWeekStartDate())
              }
              title="Go to current week"
              style={{ marginLeft: '8px', fontSize: '12px', width: 'auto', padding: '6px 12px' }}
            >
              Today
            </NavButton>
          }
        </CalendarNavigation>
        {trainingWeek && (
          <TrainingWeekTitle>
            Week {trainingWeek?.week_in_plan} / {trainingWeek?.training_phase.toUpperCase()}
          </TrainingWeekTitle>
        )}
        <Spacer></Spacer>

        <CalendarControls>
          <ViewModeDropdown $disabled={true}>
            <DropdownButton onClick={toggleViewModeDropdown}>
              {viewMode}
              <ChevronDown size={14} />
            </DropdownButton>
            <DropdownMenu $isOpen={viewModeDropdownOpen} onClick={(e) => e.stopPropagation()}>
              <DropdownItem onClick={() => handleViewModeChange('Week')}>Week</DropdownItem>
              <DropdownItem onClick={() => handleViewModeChange('Month')}>Month</DropdownItem>
            </DropdownMenu>
          </ViewModeDropdown>
          <SettingsButton onClick={toggleSettingsDropdown} title="Calendar settings">
            <Settings size={16} />
            <SettingsDropdown $isOpen={settingsDropdownOpen} onClick={(e) => e.stopPropagation()}>
              <SettingsItem>
                {/* model is not mature yet
                <span className="setting-label">Show Recovery</span>
                <div className="setting-control">
                  <ToggleSwitch
                    $isOn={settings.showRecoveryIndicator}
                    onClick={() => handleSettingToggle('showRecoveryIndicator')}
                  />
                </div>
                */}
              </SettingsItem>
              <SettingsItem>
                <span className="setting-label">Discipline Colors</span>
                <div className="setting-control">
                  <ToggleSwitch
                    $isOn={settings.showDisciplineColors}
                    onClick={() => handleSettingToggle('showDisciplineColors')}
                  />
                </div>
              </SettingsItem>
              <SettingsItem>
                <span className="setting-label">Daily Weather</span>
                <div className="setting-control">
                  <ToggleSwitch
                    $isOn={settings.showWeatherIndicator}
                    onClick={() => handleSettingToggle('showWeatherIndicator')}
                  />
                </div>
              </SettingsItem>
              <SettingsItem>
                <span className="setting-label">Nightly Sleep</span>
                <div className="setting-control">
                  <ToggleSwitch
                    $isOn={settings.showSleepIndicator}
                    onClick={() => handleSettingToggle('showSleepIndicator')}
                  />
                </div>
              </SettingsItem>
            </SettingsDropdown>
          </SettingsButton>

          {isFeatureEnabled(FeatureFlagKey.GenerateWorkouts) && (
            <ReplanButton onClick={generateWeeklyWorkouts} disabled={isGenerating} $loading={isGenerating}>
              <span>
                {isGenerating
                  ? 'Generating...'
                  : workouts.some((workout) =>
                        weekDates.some((date) => workout.scheduled_date === formatDateForComparison(date))
                      )
                    ? 'Replan Workouts'
                    : 'Plan Workouts'}
              </span>
            </ReplanButton>
          )}

          {isFeatureEnabled(FeatureFlagKey.CalDavSharing) && (
            <CalendarShareButton
              isOpen={calendarShareDropdownOpen}
              calendarUrl={calendarUrl}
              isLoadingCalendarUrl={isLoadingCalendarUrl}
              onToggleDropdown={toggleCalendarShareDropdown}
              onGenerateCalendarUrl={handleGenerateCalendarUrl}
              onCopyCalendarUrl={handleCopyCalendarUrl}
            />
          )}

          <SummaryToggleButton
            $isActive={showSummaryPanel}
            onClick={() => {
              setShowSummaryPanel(!showSummaryPanel)
              handleSettingToggle('showCalendarSummary')
            }}
            title={showSummaryPanel ? 'Hide Summary Panel' : 'Show Summary Panel'}
          >
            {showSummaryPanel ? <PanelRightClose size={16} /> : <PanelRightOpen size={16} />}
          </SummaryToggleButton>
        </CalendarControls>
      </CalendarHeader>
      <CalendarWithSummary>
        <CalendarSection>
          <CalendarContainer>
            {workoutsError && (
              <div
                style={{
                  padding: theme.spacing.lg,
                  textAlign: 'center',
                  color: theme.colors.error || '#f44336',
                  fontSize: theme.typography.sm,
                }}
              >
                Error loading workouts: {workoutsError}
              </div>
            )}
            <CalendarGrid>
              {isLoadingWorkouts
                ? // Show skeleton loaders for each day
                  displayDates.map((date) => {
                    const isToday = formatDateForComparison(new Date()) === formatDateForComparison(date)
                    return (
                      <DayColumn key={date.toISOString()}>
                        <DayHeader $isToday={isToday}>
                          <DayHeaderTop>
                            <DayName $isToday={isToday}>{formatDayName(date)}</DayName>
                            <DayNumber $isToday={isToday}>{formatDateNumber(date)}</DayNumber>
                          </DayHeaderTop>
                          <DayHeaderContent>{/* Empty content to match structure */}</DayHeaderContent>
                        </DayHeader>
                        <DayContent>
                          <SkeletonWrapper>
                            <Skeleton height={60} count={2} style={{ marginBottom: 8 }} />
                          </SkeletonWrapper>
                        </DayContent>
                      </DayColumn>
                    )
                  })
                : displayDates.map((date) => {
                    const dateString = formatDateForComparison(date)
                    const dayWorkouts = workouts.filter((workout) => workout.scheduled_date === dateString)
                    const isToday = formatDateForComparison(new Date()) === dateString
                    const daySleep = getSleepDataForDate(date)

                    const entries = getEntriesForDate(date)
                    const allDayEntries = entries.filter((entry) => !entry.start_time && !entry.end_time)
                    const nonRestDayEntries = entries.filter((entry) => {
                      return entry.entry_type !== 'rest_day' && !(!entry.start_time && !entry.end_time)
                    })

                    return (
                      <DayColumn
                        key={date.toISOString()}
                        $isDragOver={dragOverDay === dateString}
                        onDragOver={handleDragOver}
                        onDragEnter={(e) => handleDragEnter(e, dateString)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, dateString)}
                      >
                        <DayHeader
                          $isToday={isToday}
                          onClick={(e) => {
                            if (!isFeatureEnabled(FeatureFlagKey.AddAllDayEvents)) return
                            e.preventDefault()
                            e.stopPropagation()
                            setModalState({
                              isOpen: true,
                              mode: 'create',
                              defaultDate: dateString,
                              workout: undefined,
                            })
                            setEntryType('event')
                          }}
                        >
                          <DayHeaderTop>
                            <DayName $isToday={isToday}>{formatDayName(date)}</DayName>
                            <DayNumber $isToday={isToday}>{formatDateNumber(date)}</DayNumber>
                          </DayHeaderTop>

                          <DayHeaderContent>
                            {showWeatherIndicator &&
                              (() => {
                                const weatherForDay = getWeatherForDate(date)
                                if (!weatherForDay) return <WeatherContainer></WeatherContainer>

                                const WeatherIconComponent = getWeatherIcon(
                                  weatherForDay.weather[0]?.main,
                                  weatherForDay.weather[0]?.icon
                                )
                                return (
                                  <WeatherContainer>
                                    <WeatherIcon $isToday={isToday}>
                                      <WeatherIconComponent size={14} />
                                    </WeatherIcon>
                                    <WeatherTemp $isToday={isToday}>{Math.round(weatherForDay.temp.day)}°C</WeatherTemp>
                                  </WeatherContainer>
                                )
                              })()}

                            {settings.showSleepIndicator && sleepData.length > 0 && (
                              <SleepIndicator>
                                {daySleep && date <= new Date() && (
                                  <>
                                    <SleepIcon $isToday={isToday}>
                                      <Moon size={10} />
                                    </SleepIcon>
                                    <SleepBar $duration={daySleep.duration} $isToday={isToday} />
                                    <SleepDuration $isToday={isToday}>
                                      {formatDurationMin(daySleep.duration * 60)}
                                    </SleepDuration>
                                    <SleepTooltip>
                                      {daySleep.bedtime && `Bed: ${daySleep.bedtime}`}
                                      <br />
                                      Target Reached:{' '}
                                      {daySleep.duration >= 7 ? 'Yes' : daySleep.duration >= 6 ? 'Almost' : 'No'}
                                    </SleepTooltip>
                                  </>
                                )}
                              </SleepIndicator>
                            )}

                            {allDayEntries.map((entry) => (
                              <AllDayEvent
                                key={entry.id}
                                $isToday={isToday}
                                onClick={(e) => {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  handleEntryClick(entry)
                                }}
                              >
                                <AllDayIcon $isToday={isToday}>
                                  {React.createElement(getEntryIcon(entry.entry_type), { size: 12 })}
                                </AllDayIcon>
                                <AllDayText>{entry.title || entry.entry_type + 'Event'}</AllDayText>
                              </AllDayEvent>
                            ))}
                          </DayHeaderContent>
                        </DayHeader>
                        <DayContent>
                          {dayWorkouts.length > 0 ? (
                            <>
                              {dayWorkouts.map((workout) => {
                                const recoveryData = calculateWorkoutRecoveryData(workout, dateString, workouts)

                                return (
                                  <div key={`${workout.id}-${settings.showRecoveryIndicator}`}>
                                    {settings.showRecoveryIndicator && (
                                      <RecoveryIndicator $recoveryLevel={recoveryData.level}>
                                        <Tooltip>Recovery remaining: {formatRecoveryHours(recoveryData.hours)}</Tooltip>
                                      </RecoveryIndicator>
                                    )}
                                    <WorkoutItem
                                      workout={workout}
                                      isDragging={draggedWorkout === workout.id}
                                      showDisciplineColors={settings.showDisciplineColors}
                                      onDragStart={handleDragStart}
                                      onDragEnd={handleDragEnd}
                                      onMouseDown={(e) => handleWorkoutMouseDown(e, workout)}
                                      onClick={(e) => handleWorkoutClick(e, workout)}
                                      onEdit={() => handleEditWorkout(workout)}
                                      onDelete={(e, id) => {
                                        e.stopPropagation()
                                        e.preventDefault()
                                        handleDeleteWorkout(id)
                                      }}
                                    />
                                  </div>
                                )
                              })}
                              {dragOverDay === dateString && draggedWorkout && (
                                <DropIndicator>Add workout to this day</DropIndicator>
                              )}
                            </>
                          ) : (
                            <></>
                          )}

                          {nonRestDayEntries.map((entry) => (
                            <WorkoutItem
                              key={entry.id}
                              entry={entry}
                              isDragging={false}
                              showDisciplineColors={settings.showDisciplineColors}
                              onDragStart={handleDragStart}
                              onDragEnd={handleDragEnd}
                              onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                handleEntryClick(entry)
                              }}
                              onEdit={() => handleEditEntry(entry)}
                              onDelete={(e, id) => {
                                e.stopPropagation()
                                e.preventDefault()
                                handleDeleteEntry(id)
                              }}
                            />
                          ))}

                          {dayWorkouts.length === 0 && nonRestDayEntries.length === 0 ? (
                            <EmptyDay>No Entries</EmptyDay>
                          ) : (
                            <></>
                          )}
                          {dragOverDay === dateString && draggedWorkout && (
                            <DropIndicator>Release to move workout</DropIndicator>
                          )}
                          {isFeatureEnabled(FeatureFlagKey.AddEntries) && (
                            <AddEntryButton onClick={() => handleAddWorkout(dateString)}>+ Add Entry</AddEntryButton>
                          )}
                        </DayContent>
                      </DayColumn>
                    )
                  })}
            </CalendarGrid>
          </CalendarContainer>
        </CalendarSection>
        {showSummaryPanel && (
          <WeekSummaryPanel
            athleteId={athleteId ?? ''}
            workouts={workouts}
            weekDates={weekDates}
            targetTSS={400}
            targetHours={8}
          />
        )}
      </CalendarWithSummary>

      <WorkoutFormModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        onFullSize={handleFullSize}
        initialData={modalState.workout}
        initialEntry={modalState.calendarEntry}
        defaultDate={modalState.defaultDate}
        defaultEntryType={modalState.entryType}
      />

      {constraintsModalState.constraints && (
        <GenerateWorkoutConstraintsModal
          initialData={constraintsModalState.constraints}
          isOpen={constraintsModalState.isOpen}
          onClose={() => setConstraintsModalState({ isOpen: false })}
          onSubmit={handleConstraintsSubmit}
          isGenerating={isGenerating}
        />
      )}

      <WorkoutDetailModal
        isOpen={detailModalState.isOpen}
        onClose={closeDetailModal}
        onEdit={handleDetailEdit}
        onDelete={handleDetailDelete}
        onComplete={handleWorkoutCompletion}
        onSchedule={handleSchedule}
        workout={detailModalState.workout}
        entry={detailModalState.entry}
      />
    </DesktopCard>
  )
}
